import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { loadState, saveState } from '../utils/localStorage';
import { createProximitySensor } from '../utils/proximitySensor';
import { getProgressionLevels, getLevelLabel } from '../utils/progression';
import { addHistoryEntry } from '../utils/history';
import useWakeLock from '../hooks/useWakeLock';
import RestTimer from '../components/Home/RestTimer';
import LevelCompleteModal from '../components/Home/LevelCompleteModal';
import GiveUpButton from '../components/Home/GiveUpButton';

const COOLDOWN_MS = 600;
const SETS_PER_LEVEL = 5;
const REST_SECONDS = 60;

const HomeView = () => {
  const [state, setState] = useState(() => loadState() || {
    totalPushups: 0,
    userProgress: { currentLevelIndex: 0, currentSetIndex: 0, consecutiveFails: 0, preDeloadLevelIndex: null, deloadWorkoutsRemaining: 0 },
    settings: { soundEnabled: true }
  });
  const [repCount, setRepCount] = useState(0);
  const [cooldown, setCooldown] = useState(false);
  const [workoutComplete, setWorkoutComplete] = useState(false);
  const [showChoice, setShowChoice] = useState(false);
  const [proximitySupported, setProximitySupported] = useState(true);
  const [resting, setResting] = useState(false);
  const [restTime, setRestTime] = useState(REST_SECONDS);
  const [hasStarted, setHasStarted] = useState(false);
  const { t } = useTranslation();
  const repAudio = useRef(null);
  const sensorRef = useRef(null);
  const lastState = useRef('up');
  const navigate = useNavigate();
  const levels = useMemo(() => getProgressionLevels(), []);
  const currentLevel = state.userProgress.currentLevelIndex || 0;
  const setIndex = state.userProgress.currentSetIndex || 0;
  const targetReps = levels[currentLevel]?.reps[setIndex] || 0;

  useWakeLock();

  useEffect(() => {
    let supported = true;
    sensorRef.current = createProximitySensor(handleProximity);
    if (!('ProximitySensor' in window) && !('ondeviceproximity' in window)) {
      supported = false;
    }
    setProximitySupported(supported);
    return () => {
      if (sensorRef.current && sensorRef.current.stop) sensorRef.current.stop();
    };
    // eslint-disable-next-line
  }, []);

  function handleProximity(distance) {
    if (workoutComplete || resting) return;
    if (cooldown) return;
    if (distance < 5 && lastState.current === 'up') {
      lastState.current = 'down';
      countRep();
    } else if (distance >= 5 && lastState.current === 'down') {
      lastState.current = 'up';
    }
  }

  function countRep() {
    setCooldown(true);
    setTimeout(() => setCooldown(false), COOLDOWN_MS);
    setRepCount(c => setIndex === 4 ? c + 1 : Math.max(c - 1, 0));
    setHasStarted(true);
    if (state?.settings?.soundEnabled !== false && repAudio.current) {
      repAudio.current.currentTime = 0;
      repAudio.current.play();
    }
  }

  function handleClickWorkoutArea(e) {
    // Allow click anywhere as fallback, but prefer clicking the ring
    // (no-op, logic moved to ring itself)
  }

  function handleRingClick(e) {
    if (!cooldown && !workoutComplete && !resting && (setIndex === 4 || repCount > 0)) {
      countRep();
    }
  }

  // Initialize rep count only on component mount
  useEffect(() => {
    const initialTarget = levels[currentLevel]?.reps[setIndex] || 0;
    setRepCount(setIndex === 4 ? 0 : initialTarget);
    setHasStarted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  useEffect(() => {
    if (setIndex < 4 && hasStarted && repCount === 0 && targetReps > 0) {
      // Log set to history
      const today = new Date().toISOString().slice(0, 10);
      addHistoryEntry(today, targetReps);
      // Next set in this level, start rest
      setResting(true);
      setRestTime(REST_SECONDS);
    }
  }, [repCount, targetReps, setIndex, currentLevel, state, hasStarted]);

  // Rest timer effect
  useEffect(() => {
    if (!resting) return;
    if (restTime === 0) {
      // Play double beep
      if (state?.settings?.soundEnabled !== false && repAudio.current) {
        repAudio.current.currentTime = 0;
        repAudio.current.play();
        setTimeout(() => {
          if (repAudio.current) { // Check if component is still mounted
            repAudio.current.currentTime = 0;
            repAudio.current.play();
          }
        }, 500); // Delay for the second beep
      }

      // Next set
      setState(prevState => {
        const newState = {
          ...prevState,
          totalPushups: (prevState.totalPushups || 0) + targetReps,
          userProgress: {
            ...prevState.userProgress,
            currentLevelIndex: currentLevel,
            currentSetIndex: setIndex + 1
          }
        };
        saveState(newState);
        return newState;
      });
      // Reset repCount for the new set
      const nextTarget = levels[currentLevel]?.reps[setIndex + 1] || 0;
      setRepCount(setIndex + 1 === 4 ? 0 : nextTarget);
      setHasStarted(false);
      setResting(false);
      return;
    }
    const timer = setTimeout(() => setRestTime(t => t - 1), 1000);
    return () => clearTimeout(timer);
  }, [resting, restTime, targetReps, setIndex, currentLevel, levels, repAudio, state?.settings?.soundEnabled]);

  function skipRest() {
    setRestTime(0);
  }

  function handleFinishAMRAP() {
    if (!hasStarted) return; // Prevent finishing without doing reps

    const actualReps = repCount;
    const delta = actualReps - targetReps;
    let newLevelIndex = currentLevel;
    let consecutiveFails = state.userProgress?.consecutiveFails || 0;
    let deloadWorkoutsRemaining = state.userProgress?.deloadWorkoutsRemaining || 0;
    let preDeloadLevelIndex = state.userProgress?.preDeloadLevelIndex || null;

    if (delta >= 3) {
      newLevelIndex = Math.min(currentLevel + 2, levels.length - 1);
      consecutiveFails = 0;
    } else if (delta >= 0 && delta <= 2) {
      consecutiveFails = 0;
    } else {
      consecutiveFails += 1;
    }

    if (consecutiveFails >= 3) {
      preDeloadLevelIndex = currentLevel;
      deloadWorkoutsRemaining = 3;
      newLevelIndex = Math.max(0, Math.floor(currentLevel * 0.7)); // Drop by ~30%
      consecutiveFails = 0;
    } else if (deloadWorkoutsRemaining > 0) {
      deloadWorkoutsRemaining -= 1;
      if (deloadWorkoutsRemaining === 0 && preDeloadLevelIndex !== null) {
        newLevelIndex = preDeloadLevelIndex;
        preDeloadLevelIndex = null;
      }
    }

    const today = new Date().toISOString().slice(0, 10);
    addHistoryEntry(today, actualReps);

    setState(prevState => {
      const newState = {
        ...prevState,
        totalPushups: (prevState.totalPushups || 0) + actualReps,
        userProgress: {
          ...prevState.userProgress,
          currentLevelIndex: newLevelIndex,
          currentSetIndex: 0,
          consecutiveFails,
          preDeloadLevelIndex,
          deloadWorkoutsRemaining
        }
      };
      saveState(newState);
      return newState;
    });

    setWorkoutComplete(true);
    setShowChoice(true);
  }

  function handleChoice(next) {
    // Just navigate and reset state since progression is already handled
    const newState = loadState();
    setState(newState);
    const newLevel = newState.userProgress.currentLevelIndex;
    const newTargetReps = levels[newLevel]?.reps[0] || 0;
    setRepCount(newTargetReps);
    setHasStarted(false);
    setWorkoutComplete(false);
    setShowChoice(false);
    navigate('/');
  }

  function handleGiveUp() {
    // No confirmation popup
    const newLevel = currentLevel; // Keep current level
    const newState = {
      ...state,
      userProgress: {
        currentLevelIndex: newLevel,
        currentSetIndex: 0 // Reset to the beginning of the current level
      }
    };
    setState(newState);
    saveState(newState);
    // Reset rep count for the current level
    const newTargetReps = levels[newLevel]?.reps[0] || 0;
    setRepCount(newTargetReps);
    setHasStarted(false);
    setWorkoutComplete(false);
    setShowChoice(false);
    navigate('/'); // Navigate to home or workout view
  }

  if (!levels[currentLevel]) return <div style={{ padding: 40 }}>No level found.</div>;

  return (
    <div
      style={{
        minHeight: '100vh',
        background: '#fbeff3',
        fontFamily: 'Inter, Roboto, Segoe UI, Arial, sans-serif',
        color: '#333',
        display: 'flex',
        flexDirection: 'column',
        padding: 0,
        margin: 0,
      }}
      onClick={handleClickWorkoutArea}
    >
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', alignItems: 'center' }}>
        <div className="pretty-section home-section">
          <h2 className="pretty-title">{levels[currentLevel].name} <span className="pretty-sub">({getLevelLabel(currentLevel)})</span></h2>
          <div style={{ marginBottom: 8 }}>{t('level_x', { level: setIndex + 1 })} of {SETS_PER_LEVEL}</div>
          <div style={{ marginBottom: 16 }}>
            {setIndex === 4 ? `AMRAP (Target: ${targetReps})` : t('target_reps', { count: targetReps })}
          </div>
          <div className="pretty-reps">{repCount}</div>
          <div className="pretty-nose" onClick={handleRingClick} style={{ cursor: 'pointer', marginBottom: setIndex === 4 ? '16px' : '0' }}>
            <span style={{ color: '#b85b7b' }}>{t('nose_here')}</span>
          </div>
          {setIndex === 4 && !workoutComplete && (
            <button
              className="pretty-btn"
              style={{ background: 'linear-gradient(90deg, #f7a8c7 0%, #f8bfcf 100%)', marginTop: '16px' }}
              onClick={handleFinishAMRAP}
            >
              Finish AMRAP Set
            </button>
          )}
          {!proximitySupported && <div style={{ color: '#888', fontSize: 14, marginTop: 8 }}>({t('click_to_count')})</div>}
          {cooldown && <div style={{ color: '#f7a8c7' }}>{t('cooldown')}</div>}
          <RestTimer resting={resting} restTime={restTime} skipRest={skipRest} t={t} />
          <LevelCompleteModal showChoice={showChoice} handleChoice={handleChoice} t={t} />
          <audio ref={repAudio} src="/beep.mp3" preload="auto" />
        </div>
        <GiveUpButton handleGiveUp={handleGiveUp} t={t} />
      </div>
    </div>
  );
};

export default HomeView;
